import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import { Checkbox } from '../checkbox';

import {
  DropdownRootStyled,
  DropdownTriggerStyled,
  DropdownOptionsStyled,
  DropdownTitleStyled,
  DropdownItemStyled,
  DropdownSeparatorStyled,
} from './style';

// --- Placement (Bootstrap-style preventOverflow, no Popper) -----------------

const FLIP_MARGIN = 8;
const FLIP_DEBOUNCE_MS = 100;
const HYSTERESIS_MARGIN = 24;

export type Position = 'left' | 'right' | 'center' | 'justified';
export type Vertical = 'top' | 'bottom';

interface Rect {
  top: number;
  left: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

interface Viewport {
  width: number;
  height: number;
}

interface PreventOverflowOptions {
  flipMargin: number;
  hysteresisMargin: number;
}

interface PlacementState {
  position: Position;
  vertical: Vertical;
}

/** Pure: compute effective placement so the menu stays within viewport (Bootstrap preventOverflow-style). */
function computePreventOverflowPlacement(
  menuRect: Rect,
  viewport: Viewport,
  preferred: PlacementState,
  current: PlacementState,
  options: PreventOverflowOptions,
): PlacementState {
  const { flipMargin, hysteresisMargin } = options;
  const { position: pPos, vertical: pVert } = preferred;
  let position: Position = current.position;
  let vertical: Vertical = current.vertical;

  const overflowRight = menuRect.right > viewport.width - flipMargin;
  const overflowLeft = menuRect.left < flipMargin;
  const overflowBottom = menuRect.bottom > viewport.height - flipMargin;
  const overflowTop = menuRect.top < flipMargin;
  const hasRoomRight = menuRect.right + menuRect.width < viewport.width - hysteresisMargin;
  const hasRoomLeft = menuRect.left - menuRect.width > hysteresisMargin;
  const hasRoomBottom = menuRect.bottom + menuRect.height < viewport.height - hysteresisMargin;
  const hasRoomTop = menuRect.top - menuRect.height > hysteresisMargin;

  const prefersRight = pPos === 'right' || pPos === 'center' || pPos === 'justified';
  const prefersLeft = pPos === 'left';

  if (
    overflowRight &&
    (position === 'right' || position === 'center' || position === 'justified')
  ) {
    position = 'left';
  } else if (hasRoomRight && position === 'left' && prefersRight) {
    position = pPos;
  } else if (overflowLeft && position === 'left') {
    position = 'right';
  } else if (hasRoomLeft && position === 'right' && prefersLeft) {
    position = 'left';
  }

  if (overflowBottom && vertical === 'bottom') {
    vertical = 'top';
  } else if (hasRoomBottom && vertical === 'top' && pVert === 'bottom') {
    vertical = 'bottom';
  } else if (overflowTop && vertical === 'top') {
    vertical = 'bottom';
  } else if (hasRoomTop && vertical === 'bottom' && pVert === 'top') {
    vertical = 'top';
  }

  return { position, vertical };
}

function getViewport(): Viewport {
  return {
    width: document.documentElement.clientWidth,
    height: document.documentElement.clientHeight,
  };
}

// --- Context -----------------------------------------------------------------

export interface DropdownContextValue {
  rootRef: React.RefObject<HTMLDivElement | null>;
  trackClick: boolean;
  clickPosition: { x: number; y: number } | null;
  setClickPosition: (x: number, y: number) => void;
}

const DropdownContext = createContext<DropdownContextValue | null>(null);

// Forward ref version of DropdownRoot with click-outside behavior
const DropdownRootWithRef = forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof DropdownRootStyled> & {
    onClose?: () => void;
    isOpen?: boolean;
    trackClick?: boolean;
  }
>(({ onClose, isOpen, trackClick = true, ...props }, ref) => {
  const internalRef = useRef<HTMLDivElement>(null);
  const [clickPosition, setClickPositionState] = useState<{ x: number; y: number } | null>(null);

  const setClickPosition = useCallback((x: number, y: number) => {
    setClickPositionState({ x, y });
  }, []);

  const setRef = useCallback(
    (el: HTMLDivElement | null) => {
      (internalRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
      if (typeof ref === 'function') (ref as (el: HTMLDivElement | null) => void)(el);
      else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
    },
    [ref],
  );

  useEffect(() => {
    if (!isOpen || !onClose) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (internalRef.current && !internalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const contextValue: DropdownContextValue = {
    rootRef: internalRef,
    trackClick,
    clickPosition,
    setClickPosition,
  };

  return (
    <DropdownContext.Provider value={contextValue}>
      <DropdownRootStyled ref={setRef} {...props} />
    </DropdownContext.Provider>
  );
});

// Trigger wrapper: captures click position when trackClick is true
const DropdownTriggerWithClickCapture = forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof DropdownTriggerStyled>
>(({ onClick, ...props }, ref) => {
  const ctx = useContext(DropdownContext);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (ctx?.trackClick) {
        ctx.setClickPosition(e.clientX, e.clientY);
      }
      onClick?.(e);
    },
    [ctx, onClick],
  );

  return <DropdownTriggerStyled ref={ref} onClick={handleClick} {...props} />;
});

DropdownTriggerWithClickCapture.displayName = 'DropdownTriggerWithClickCapture';

interface DropdownOptionsWrapperProps extends React.HTMLAttributes<HTMLDivElement> {
  position?: Position;
  vertical?: Vertical;
  open?: boolean;
  width?: string;
  flipToFit?: boolean;
  flipMargin?: number;
}

/** Schedules placement update on open + resize (Bootstrap update()-style), with debounce. */
function usePreventOverflow(
  rootRef: React.RefObject<HTMLDivElement | null> | null,
  optionsRef: React.RefObject<HTMLDivElement | null>,
  preferredPosition: Position,
  preferredVertical: Vertical,
  open: boolean,
  flipToFit: boolean,
  flipMargin: number,
): PlacementState {
  const preferred: PlacementState = { position: preferredPosition, vertical: preferredVertical };
  const [effective, setEffective] = useState<PlacementState>(preferred);
  const effectiveRef = useRef(effective);
  effectiveRef.current = effective;

  const runUpdate = useCallback(() => {
    if (!optionsRef.current) return;
    const menuRect = optionsRef.current.getBoundingClientRect() as Rect;
    const viewport = getViewport();
    const current = effectiveRef.current;
    const preferredState: PlacementState = {
      position: preferredPosition,
      vertical: preferredVertical,
    };
    const next = computePreventOverflowPlacement(menuRect, viewport, preferredState, current, {
      flipMargin,
      hysteresisMargin: HYSTERESIS_MARGIN,
    });
    if (next.position !== current.position || next.vertical !== current.vertical) {
      setEffective(next);
    }
  }, [optionsRef, preferredPosition, preferredVertical, flipMargin]);

  useLayoutEffect(() => {
    const root = rootRef?.current;
    if (!open || !flipToFit || !root || !optionsRef.current) {
      if (!open) setEffective(preferred);
      return;
    }

    runUpdate();

    let debounceId: ReturnType<typeof setTimeout> | null = null;
    const schedule = () => {
      if (debounceId != null) clearTimeout(debounceId);
      debounceId = setTimeout(() => {
        debounceId = null;
        requestAnimationFrame(runUpdate);
      }, FLIP_DEBOUNCE_MS);
    };

    const ro = new ResizeObserver(schedule);
    ro.observe(root);
    let el: HTMLElement | null = root.parentElement;
    for (let i = 0; i < 8 && el && el !== document.body; i++) {
      ro.observe(el);
      el = el.parentElement;
    }
    window.addEventListener('resize', schedule);

    return () => {
      if (debounceId != null) clearTimeout(debounceId);
      ro.disconnect();
      window.removeEventListener('resize', schedule);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- preferred is derived from preferredPosition/preferredVertical
  }, [open, flipToFit, rootRef, optionsRef, preferredPosition, preferredVertical, runUpdate]);

  return open && flipToFit ? effective : preferred;
}

const CLICK_ANCHOR_OFFSET = 4;

const DropdownOptionsWithFlip = forwardRef<HTMLDivElement, DropdownOptionsWrapperProps>(
  (
    {
      position: preferredPosition,
      vertical: preferredVertical,
      open,
      flipToFit = true,
      flipMargin = FLIP_MARGIN,
      ...rest
    },
    ref,
  ) => {
    const ctx = useContext(DropdownContext);
    const rootRef = ctx?.rootRef ?? null;
    const optionsRef = useRef<HTMLDivElement>(null);

    const position = preferredPosition ?? 'center';
    const vertical = preferredVertical ?? 'bottom';
    const effective = usePreventOverflow(
      rootRef,
      optionsRef,
      position,
      vertical,
      !!open,
      flipToFit,
      flipMargin,
    );

    const useClickPosition = !!ctx?.trackClick && !!ctx?.clickPosition && open;
    const clickAnchor =
      useClickPosition && ctx?.clickPosition
        ? { x: ctx.clickPosition.x, y: ctx.clickPosition.y + CLICK_ANCHOR_OFFSET }
        : undefined;

    const setOptionsRef = useCallback(
      (el: HTMLDivElement | null) => {
        (optionsRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
        if (typeof ref === 'function') (ref as (el: HTMLDivElement | null) => void)(el);
        else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
      },
      [ref],
    );

    return (
      <DropdownOptionsStyled
        ref={setOptionsRef}
        position={useClickPosition ? 'center' : effective.position}
        vertical={useClickPosition ? 'bottom' : effective.vertical}
        open={open}
        clickAnchor={clickAnchor}
        {...rest}
      />
    );
  },
);

DropdownOptionsWithFlip.displayName = 'DropdownOptionsWithFlip';

interface DropdownComposite {
  Root: typeof DropdownRootWithRef;
  Trigger: typeof DropdownTriggerWithClickCapture;
  Options: typeof DropdownOptionsWithFlip;
  Title: typeof DropdownTitleStyled;
  Item: typeof DropdownItemStyled;
  Separator: typeof DropdownSeparatorStyled;
}

const Dropdown: DropdownComposite = {
  Root: DropdownRootWithRef,
  Trigger: DropdownTriggerWithClickCapture,
  Options: DropdownOptionsWithFlip,
  Title: DropdownTitleStyled,
  Item: DropdownItemStyled,
  Separator: DropdownSeparatorStyled,
};

export { Dropdown };

// Container for the dropdown
const DropdownContainer = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 1000;
  background-color: ${({ theme }) => theme.dropdown.optionBackgroundColor};
  border: 1px solid ${({ theme }) => theme.dropdown.dividerColor};
  border-radius: 4px;
  box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.15);
  width: 151px;
  padding: 0px 0px 4px;
  margin-top: 4px;
`;

// Individual menu header component
const MenuHeaderContainer = styled.div`
  padding: 8px 0px 4px;
  border-bottom: 1px solid ${({ theme }) => theme.dropdown.dividerColor};
`;

const MenuHeaderText = styled.div`
  font-family: 'Open Sans';
  font-weight: 700;
  font-size: 9px;
  line-height: 1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.dropdown.titleTextColor};
  padding: 0px 9px 6px;
`;

// Individual menu item component
const MenuItemContainer = styled.div`
  padding: 4px 8px;
  cursor: pointer;
  font-family: 'Open Sans';
  font-weight: 400;
  font-size: 11px;
  line-height: 1.1818em;
  color: ${({ theme }) => theme.dropdown.optionTextColor};
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: ${({ theme }) => theme.dropdown.itemHoverBackgroundColor};
  }
`;

// Separator between groups
const MenuSeparatorContainer = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.dropdown.dividerColor};
  margin: 4px 0;
`;

export interface DropdownItem {
  id: string;
  label: string;
  onClick: () => void;
}

export interface DropdownGroup {
  id: string;
  headerText: string;
  items: DropdownItem[];
}

interface DropdownProps {
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}

export const DropdownV2: React.FC<DropdownProps> = ({ className, style, children }) => (
  <DropdownContainer className={className} style={style}>
    {children}
  </DropdownContainer>
);

// Menu header component
interface MenuHeaderProps {
  children: React.ReactNode;
}

export const MenuHeader: React.FC<MenuHeaderProps> = ({ children }) => (
  <MenuHeaderContainer>
    <MenuHeaderText>{children}</MenuHeaderText>
  </MenuHeaderContainer>
);

// Menu item component
interface MenuItemProps {
  onClick?: () => void;
  children: React.ReactNode;
  checkbox?: {
    checked: boolean;
    onChange: () => void;
  };
}

export const MenuItem: React.FC<MenuItemProps> = ({ onClick, children, checkbox }) => (
  <MenuItemContainer
    onClick={(e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      onClick?.();
    }}
  >
    {checkbox && <Checkbox checked={checkbox.checked} onChange={checkbox.onChange} />}
    {children}
  </MenuItemContainer>
);
// Menu separator component
export const MenuSeparator: React.FC = () => <MenuSeparatorContainer />;
